import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { staffAPI } from '../../../config/api';
import APP_COLOURS from '../../../config/colors';

const StaffAttendance = ({ staffId, staffName }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [hoveredRow, setHoveredRow] = useState(null);

  useEffect(() => {
    fetchAttendance();
  }, [staffId, startDate, endDate]);

  const fetchAttendance = async () => {
    if (!staffId) return;
    
    setLoading(true);
    try {
      console.log(`Fetching attendance for staff ${staffId} from ${startDate} to ${endDate}`);
      const attendanceData = await staffAPI.getStaffAttendance(staffId, {
        startDate: startDate,
        endDate: endDate
      });
      
      console.log('=== ATTENDANCE DATA DEBUG ===');
      console.log('Full response:', JSON.stringify(attendanceData, null, 2));
      console.log('Response type:', typeof attendanceData);
      console.log('Is array?', Array.isArray(attendanceData));
      
      // Ensure we always set an array
      if (Array.isArray(attendanceData)) {
        console.log(`Setting ${attendanceData.length} attendance records (array format)`);
        if (attendanceData.length > 0) {
          console.log('First record structure:', JSON.stringify(attendanceData[0], null, 2));
        }
        setData(attendanceData);
      } else if (attendanceData && Array.isArray(attendanceData.attendance)) {
        // Handle case where API returns { attendance: [...] }
        console.log(`Setting ${attendanceData.attendance.length} attendance records (object.attendance format)`);
        console.log('Attendance summary:', {
          count: attendanceData.count,
          totalDaysPresent: attendanceData.totalDaysPresent,
          totalHours: attendanceData.totalHours
        });
        if (attendanceData.attendance.length > 0) {
          console.log('First record structure:', JSON.stringify(attendanceData.attendance[0], null, 2));
          console.log('Available date fields in first record:', {
            date: attendanceData.attendance[0].date,
            dateCreated: attendanceData.attendance[0].dateCreated,
            checkin: attendanceData.attendance[0].checkin,
            checkout: attendanceData.attendance[0].checkout,
            clockIn: attendanceData.attendance[0].clockIn,
            clockOut: attendanceData.attendance[0].clockOut
          });
        }
        setData(attendanceData.attendance);
      } else if (attendanceData && Array.isArray(attendanceData.data)) {
        // Handle case where API returns { data: [...] }
        console.log(`Setting ${attendanceData.data.length} attendance records (object.data format)`);
        setData(attendanceData.data);
      } else if (attendanceData && typeof attendanceData === 'object') {
        // Handle case where API returns an object with other structure
        console.warn('Unexpected attendance data structure:', attendanceData);
        setData([]);
      } else {
        console.warn('No attendance data received');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely format timestamps
  const formatTime = (timeValue) => {
    if (!timeValue) return '--';
    
    try {
      // Handle Firestore Timestamp objects
      if (timeValue._seconds || (timeValue.seconds && timeValue.nanoseconds)) {
        const timestamp = timeValue._seconds || timeValue.seconds;
        return moment.unix(timestamp).format('HH:mm');
      }
      
      // Handle regular date strings/objects
      return moment(timeValue).format('HH:mm');
    } catch (error) {
      console.error('Error formatting time:', error, timeValue);
      return '--';
    }
  };

  const calculateTotals = () => {
    // Ensure data is an array
    const dataArray = Array.isArray(data) ? data : [];
    
    if (dataArray.length === 0) return { totalDays: 0, newCustomers: 0, existingCustomers: 0, totalSales: 0 };
    
    // Calculate totals only from records that match dates in the selected range
    const dateRange = generateDateRange();
    let totalDays = 0;
    let newCustomers = 0;
    let existingCustomers = 0;
    let totalSales = 0;
    
    dateRange.forEach(date => {
      const dayData = getDataForDate(date);
      if (dayData) {
        totalDays++;
        newCustomers += parseInt(dayData.newCustomer || dayData.new_customer) || 0;
        existingCustomers += parseInt(dayData.existCustomer || dayData.exist_customer || dayData.existingCustomer) || 0;
        totalSales += parseFloat(dayData.trs || dayData.TRS) || 0;
      }
    });
    
    return {
      totalDays,
      newCustomers,
      existingCustomers,
      totalSales,
    };
  };

  const generateDateRange = () => {
    const start = moment(startDate);
    const end = moment(endDate);
    const days = end.diff(start, 'days');
    
    return Array.from({ length: days + 1 }, (_, i) => 
      start.clone().add(i, 'days')
    );
  };

  const getDataForDate = (date) => {
    const dataArray = Array.isArray(data) ? data : [];
    const targetDate = date.format('YYYY-MM-DD');
    
    const found = dataArray.find(d => {
      // Try multiple possible date fields
      let recordDate = d.date || d.dateCreated || d.checkin || d.checkout || d.clockIn || d.clockOut;
      
      if (!recordDate) {
        console.log('No date field found in record:', Object.keys(d));
        return false;
      }
      
      // Handle Firestore Timestamp objects
      if (recordDate._seconds || (recordDate.seconds && recordDate.nanoseconds)) {
        const timestamp = recordDate._seconds || recordDate.seconds;
        recordDate = moment.unix(timestamp).format('YYYY-MM-DD');
      }
      
      // Try to match the date portion
      if (typeof recordDate === 'string') {
        const normalizedDate = moment(recordDate).format('YYYY-MM-DD');
        const matches = normalizedDate === targetDate;
        if (matches) {
          console.log(`Match found! Target: ${targetDate}, Record: ${normalizedDate}`);
        }
        return matches;
      }
      
      return false;
    });
    
    return found;
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: APP_COLOURS.BLUE }}></div>
        <p style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading attendance data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Filter Section */}
      <div className="p-6 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
        <div className="flex items-center gap-3 mb-2">
          <i className="fas fa-calendar-alt text-lg" style={{ color: APP_COLOURS.BLUE }}></i>
          <h4 className="text-sm font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Date Range</h4>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* From Date */}
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium mb-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              From Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg text-sm focus:outline-none transition-all duration-200"
                style={{
                  backgroundColor: APP_COLOURS.BG,
                  border: `2px solid ${APP_COLOURS.BG4}`,
                  color: APP_COLOURS.TEXTCOLOR,
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BLUE}
                onBlur={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BG4}
              />
            </div>
          </div>

          {/* Arrow Separator */}
          <div className="flex items-end pb-2">
            <i className="fas fa-arrow-right text-lg" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
          </div>

          {/* To Date */}
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium mb-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              To Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg text-sm focus:outline-none transition-all duration-200"
                style={{
                  backgroundColor: APP_COLOURS.BG,
                  border: `2px solid ${APP_COLOURS.BG4}`,
                  color: APP_COLOURS.TEXTCOLOR,
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BLUE}
                onBlur={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BG4}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-end gap-2 pb-0.5">
            <button
              onClick={fetchAttendance}
              className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm"
              style={{ 
                backgroundColor: APP_COLOURS.BLUE, 
                color: '#ffffff',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${APP_COLOURS.BLUE}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <i className="fas fa-sync-alt mr-2"></i>
              Apply Filter
            </button>
            
            <button
              onClick={() => {
                setStartDate(moment().subtract(30, 'days').format('YYYY-MM-DD'));
                setEndDate(moment().format('YYYY-MM-DD'));
              }}
              className="px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
              style={{ 
                backgroundColor: APP_COLOURS.BG4,
                color: APP_COLOURS.TEXTCOLOR,
                border: `1px solid ${APP_COLOURS.BG5}`,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = APP_COLOURS.BG5}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = APP_COLOURS.BG4}
            >
              <i className="fas fa-undo mr-2"></i>
              Reset
            </button>
          </div>
        </div>

        {/* Quick Date Filters */}
        <div className="flex items-center gap-2 mt-4 pt-4" style={{ borderTop: `1px solid ${APP_COLOURS.BG4}` }}>
          <span className="text-xs font-medium mr-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>Quick Select:</span>
          {[
            { label: 'Last 7 days', days: 7 },
            { label: 'Last 30 days', days: 30 },
            { label: 'Last 90 days', days: 90 },
            { label: 'This Year', days: moment().dayOfYear() },
          ].map((preset) => (
            <button
              key={preset.label}
              onClick={() => {
                setStartDate(moment().subtract(preset.days, 'days').format('YYYY-MM-DD'));
                setEndDate(moment().format('YYYY-MM-DD'));
              }}
              className="px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200"
              style={{ 
                backgroundColor: APP_COLOURS.BG,
                color: APP_COLOURS.TEXTCOLOR,
                border: `1px solid ${APP_COLOURS.BG4}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = APP_COLOURS.BG4;
                e.currentTarget.style.borderColor = APP_COLOURS.BLUE;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = APP_COLOURS.BG;
                e.currentTarget.style.borderColor = APP_COLOURS.BG4;
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

     

      {/* Summary Section */}
      <div className="p-6 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
        <h3 className="text-xl font-semibold mb-4" style={{ color: APP_COLOURS.TEXTCOLOR }}>
          <i className="fas fa-chart-bar mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
          Attendance Summary
        </h3>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="p-4 rounded" style={{ backgroundColor: APP_COLOURS.BG4 }}>
            <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>Total Days</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: APP_COLOURS.TEXTCOLOR }}>{totals.totalDays}</p>
          </div>
          <div className="p-4 rounded" style={{ backgroundColor: APP_COLOURS.BG4 }}>
            <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>New Customers</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: APP_COLOURS.GREEN }}>{totals.newCustomers}</p>
          </div>
          <div className="p-4 rounded" style={{ backgroundColor: APP_COLOURS.BG4 }}>
            <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>Existing Customers</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: APP_COLOURS.BLUE }}>{totals.existingCustomers}</p>
          </div>
          <div className="p-4 rounded" style={{ backgroundColor: APP_COLOURS.BG4 }}>
            <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>Total Sales</p>
            <p className="text-2xl font-semibold mt-1" style={{ color: APP_COLOURS.GREEN }}>
              R {totals.totalSales.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}
            </p>
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="rounded-lg overflow-hidden" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ minWidth: '1200px' }}>
            <thead style={{ backgroundColor: APP_COLOURS.BG4 }}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Day</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Check-in</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Lunch Start</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Lunch End</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Checkout</th>
                <th className="px-4 py-3 text-center text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>New Customers</th>
                <th className="px-4 py-3 text-center text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Exist Customers</th>
                <th className="px-4 py-3 text-center text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>IPT</th>
                <th className="px-4 py-3 text-right text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>ARS</th>
                <th className="px-4 py-3 text-right text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>TRS</th>
              </tr>
            </thead>
            <tbody>
              {generateDateRange().map((date, index) => {
                const dayData = getDataForDate(date);
                const isHovered = hoveredRow === index;
                
                return (
                  <tr
                    key={index}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      backgroundColor: isHovered ? APP_COLOURS.BG4 : 'transparent',
                      borderBottom: `1px solid ${APP_COLOURS.BG4}`,
                      opacity: dayData ? 1 : 0.5,
                    }}
                  >
                    <td className="px-4 py-3 text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      {date.format('ddd')}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      {date.format('DD/MM/YY')}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: dayData ? APP_COLOURS.TEXTCOLOR : APP_COLOURS.PLACEHOLDER }}>
                      {formatTime(dayData?.checkin || dayData?.clockIn)}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: dayData ? APP_COLOURS.TEXTCOLOR : APP_COLOURS.PLACEHOLDER }}>
                      {formatTime(dayData?.lunch_start || dayData?.lunchStart)}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: dayData ? APP_COLOURS.TEXTCOLOR : APP_COLOURS.PLACEHOLDER }}>
                      {formatTime(dayData?.lunch_end || dayData?.lunchEnd)}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: dayData ? APP_COLOURS.TEXTCOLOR : APP_COLOURS.PLACEHOLDER }}>
                      {formatTime(dayData?.checkout || dayData?.clockOut)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center" style={{ color: dayData ? APP_COLOURS.GREEN : APP_COLOURS.PLACEHOLDER }}>
                      {dayData ? (dayData.newCustomer || dayData.new_customer || 0) : '--'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center" style={{ color: dayData ? APP_COLOURS.BLUE : APP_COLOURS.PLACEHOLDER }}>
                      {dayData ? (dayData.existCustomer || dayData.exist_customer || dayData.existingCustomer || 0) : '--'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center" style={{ color: dayData ? APP_COLOURS.TEXTCOLOR : APP_COLOURS.PLACEHOLDER }}>
                      {dayData ? (dayData.ipt || dayData.IPT || '--') : '--'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right" style={{ color: dayData ? APP_COLOURS.TEXTCOLOR : APP_COLOURS.PLACEHOLDER }}>
                      {dayData && (dayData.ars || dayData.ARS) ? `R ${parseFloat(dayData.ars || dayData.ARS).toFixed(2)}` : '--'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold" style={{ color: dayData ? APP_COLOURS.GREEN : APP_COLOURS.PLACEHOLDER }}>
                      {dayData && (dayData.trs || dayData.TRS) ? `R ${parseFloat(dayData.trs || dayData.TRS).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}` : '--'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {(!Array.isArray(data) || data.length === 0) && (
          <div className="p-12 text-center">
            <i className="fas fa-calendar-times text-5xl mb-4" style={{ color: APP_COLOURS.BG5 }}></i>
            <p className="text-lg" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              No attendance data found for the selected date range
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffAttendance;

